import AppContext from 'App/AppContext';
import { isBodyVisible } from 'components/NavMain/navItems';
import { useContext, useEffect, useRef } from 'react';
import { apiGetApprovalsToDecide } from 'services/Api/approvalService';
import { apiRequest } from 'services/Auth/authConfig';
import { IHubConnection, stopHubConnection } from 'services/SignalR/GeneralHub';
import { CopilotApprovalQueueEvent, startCopilotApprovalQueueConnection } from 'services/SignalR/CopilotHub';
import {
  notifyApprovalQueueChanged,
  registerPendingApprovalsRefresher,
  setPendingApprovalCount,
} from './pendingApprovalsStore';

// How often the badge count is read while the window stays open
const pollIntervalMs = 60000;

//
// Keeps the badge count of the Approvals menu entry (pendingApprovalsStore) up to date for a licensed user: read at
// the start, every minute, when the window gets the focus, when a screen asks for it and on a queue change pushed by
// the hub (ReceiveApprovalQueueChanged, on a connection of its own), which it also passes to the open approval
// screens (notifyApprovalQueueChanged). Mounted once, by the app shell. A failed read keeps the last count: the badge
// is a hint, the queue in the Approvals scene is the record.
//
export const usePendingApprovalsPoller = (): void => {
  const appContext = useContext(AppContext);
  const appContextRef = useRef(appContext);
  appContextRef.current = appContext;
  const isEnabled = isBodyVisible(appContext);
  const tenantId = appContext.user.login.tenantId;
  const userId = appContext.user.id;

  useEffect(() => {
    if (!isEnabled) {
      setPendingApprovalCount(undefined);

      return;
    }

    let abortController: AbortController | undefined = undefined;

    const load = async () => {
      abortController?.abort();
      const controller = new AbortController();
      abortController = controller;
      try {
        const accessToken = await appContextRef.current.getAccessToken(apiRequest.scopes);
        const approvals = await apiGetApprovalsToDecide(accessToken, controller.signal);
        if (!controller.signal.aborted) setPendingApprovalCount(approvals.length);
      } catch {
        // the next interval, focus or refresh reads it again
      }
    };

    const onFocus = () => void load();
    void load();
    const timer = window.setInterval(() => void load(), pollIntervalMs);
    window.addEventListener('focus', onFocus);
    const unregister = registerPendingApprovalsRefresher(() => void load());

    //the push reaches only connections on the API instance that changed the queue: the interval stays the fallback
    let isStopped = false;
    let hubConnection: IHubConnection | null = null;
    const onQueueChanged = (event: CopilotApprovalQueueEvent) => {
      void load();
      notifyApprovalQueueChanged(event.approvalRequestId);
    };
    void startCopilotApprovalQueueConnection(appContextRef.current, onQueueChanged).then((connection) => {
      if (isStopped) {
        void stopHubConnection(connection);
      } else {
        hubConnection = connection;
      }
    });

    return () => {
      isStopped = true;
      void stopHubConnection(hubConnection);
      unregister();
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      abortController?.abort();
      setPendingApprovalCount(undefined);
    };
  }, [isEnabled, tenantId, userId]);
};
